import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, Phone, Paperclip, X, ShieldCheck } from "lucide-react";
import { COMPANY } from "../lib/constants";
import { useAuth } from "../lib/AuthContext";
import { getMissingQuoteFields, submitQuote, toJpgName } from "../lib/quoteValidation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Troca de Pneus",
  "Alinhamento",
  "Calibragem de Rodas",
  "Reparação de Furo",
  "Pastilhas ou Discos de Travão",
  "Outro / Não sei",
];

const INITIAL = {
  nome: "",
  telefone: "",
  email: "",
  veiculo: "",
  matricula: "",
  medida_pneu: "",
  quantidade: 4,
  servico: "",
  preferencia_marca: false,
  marca_preferida: "",
  mensagem: "",
  anexo_nome: "",
  anexo_base64: "",
  notify_pref: "email",
};

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5MB raw

/** Resize and re-encode image to keep payload small. Returns dataURL. */
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const QuoteForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  // If the customer is logged in, auto-fill name, email & phone (once when user
  // becomes available). We do not overwrite fields the visitor has already
  // typed on top.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      email: f.email || user.email || "",
      nome: f.nome || user.name || "",
      telefone: f.telefone || user.telefone || "",
    }));
  }, [user]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor selecione uma imagem (JPG, PNG ou HEIC).");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error("A imagem é demasiado grande (máx. 5MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, anexo_base64: dataUrl, anexo_nome: toJpgName(file.name) }));
      toast.success("Imagem anexada.");
    } catch (err) {
      toast.error("Não foi possível processar a imagem.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = () => {
    setForm((f) => ({ ...f, anexo_base64: "", anexo_nome: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const missing = getMissingQuoteFields(form);

    if (missing.length > 0) {
      const label = missing.length === 1 ? "campo em falta" : "campos em falta";
      toast.error(`Preencha o ${label}: ${missing.join(", ")}.`, {
        description: "Todos os campos são obrigatórios, exceto a mensagem.",
      });
      return;
    }

    setLoading(true);
    try {
      await submitQuote(form);
      setSuccess(true);
      setForm(INITIAL);
      toast.success("Pedido enviado com sucesso!", {
        description: "Entraremos em contacto em breve.",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Não foi possível enviar o pedido. Tente novamente ou ligue-nos.";
      toast.error(typeof msg === "string" ? msg : "Erro ao enviar pedido.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#2F568F] text-white placeholder:text-[#5A7AAE] h-12 px-4 focus:border-[#FF5A00] focus:outline-none focus:ring-1 focus:ring-[#FF5A00] transition-colors font-body";

  return (
    <section
      data-testid="quote-section"
      id="orcamento"
      className="relative pt-28 pb-14 md:pt-32 md:pb-16 bg-[#0E2452] overflow-hidden"
    >
      {/* Decorative orange bar */}
      <div className="absolute top-0 left-0 w-full h-1 bg-[#FF5A00]" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#FF5A00]/10 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#5FA8FF]/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <span data-testid="quote-label" className="section-label">
              // Pedir Orçamento
            </span>
            <h2
              data-testid="quote-title"
              className="heading-lg text-white mt-4 mb-6"
            >
              Diga-nos o que precisa.
              <br />
              <span className="text-[#FF5A00]">Nós tratamos do resto.</span>
            </h2>
            <p className="text-[#A1A1AA] font-body text-base leading-relaxed mb-8">
              Envie os dados do seu veículo e o serviço pretendido. Respondemos,
              dentro de poucas horas úteis com uma proposta clara e sem compromisso.
            </p>

            <div className="space-y-7 border-t border-[#2F568F] pt-8">
              <div>
                <div className="font-display font-bold uppercase text-white text-3xl md:text-4xl">
                  Resposta rápida
                </div>
              </div>
              <div>
                <div className="text-base md:text-lg uppercase tracking-[0.25em] text-[#A1A1AA] mb-2 font-display">
                  Orçamentos
                </div>
                <div className="font-display font-bold uppercase text-white text-3xl md:text-4xl">
                  Sem compromisso
                </div>
              </div>
              <div>
                <div className="text-base md:text-lg uppercase tracking-[0.25em] text-[#A1A1AA] mb-2 font-display">
                  Atendimento
                </div>
                <div className="font-display font-bold uppercase text-white text-3xl md:text-4xl">
                  Claro, direto e Honesto
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[#163A6D] border border-[#2F568F] p-6 md:p-7 rounded-2xl">
              {success ? (
                <div
                  data-testid="quote-success"
                  className="flex flex-col items-center justify-center text-center py-10"
                >
                  <div className="w-16 h-16 bg-[#FF5A00] flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-8 h-8 text-black" strokeWidth={2} />
                  </div>
                  <h3 className="font-display font-bold uppercase text-2xl text-white mb-3">
                    Pedido Recebido!
                  </h3>
                  <p className="text-[#A1A1AA] max-w-md mb-8 font-body">
                    Obrigado. Um dos nossos técnicos vai analisar o seu pedido e entrar
                    em contacto consigo em breve.
                  </p>
                  <button
                    data-testid="quote-reset-btn"
                    onClick={() => setSuccess(false)}
                    className="btn-outline"
                  >
                    Enviar outro pedido
                  </button>
                </div>
              ) : form.servico === "Reparação de Furo" ? (
                <div
                  data-testid="quote-puncture-notice"
                  className="py-6 md:py-8 max-w-md mx-auto"
                >
                  <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-3">
                    Serviço pretendido
                  </label>
                  <select
                    data-testid="quote-input-servico-puncture"
                    value={form.servico}
                    onChange={(e) => update("servico", e.target.value)}
                    className={`${inputCls} appearance-none cursor-pointer mb-8`}
                  >
                    <option value="" className="bg-[#163A6D]">
                      Selecione um serviço
                    </option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="bg-[#163A6D]">
                        {opt}
                      </option>
                    ))}
                  </select>

                  <div
                    data-testid="quote-puncture-card"
                    className="border-2 border-[#FF5A00] bg-gradient-to-br from-[#FF5A00]/10 to-transparent p-6 md:p-8 flex flex-col items-center text-center"
                  >
                    <div className="w-14 h-14 rounded-full bg-[#FF5A00] flex items-center justify-center mb-5">
                      <Phone className="w-7 h-7 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-bold uppercase text-white text-xl md:text-2xl mb-3">
                      Reparação de Furo
                    </h3>
                    <p className="text-[#E0E0E0] font-body text-base md:text-lg leading-relaxed mb-7 max-w-lg">
                      Se pretende a reparação de um furo, por favor contacte-nos
                      diretamente através do nosso Nº Fixo.
                    </p>
                    <a
                      href={COMPANY.landlineHref}
                      data-testid="quote-puncture-call-btn"
                      className="btn-primary text-base md:text-lg py-4 px-8 group"
                    >
                      <Phone className="w-5 h-5" />
                      {COMPANY.landline}
                    </a>
                    <p className="text-xs text-[#5FA8FF] font-body mt-5 uppercase tracking-[0.2em]">
                      Toque para ligar
                    </p>
                  </div>
                </div>
              ) : (
                <form
                  data-testid="quote-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {!user && (
                    <div
                      data-testid="optional-login-hint"
                      className="flex items-start gap-3 bg-[#0E2452] border border-[#2F568F]/60 rounded-xl px-4 py-3"
                    >
                      <ShieldCheck className="w-5 h-5 text-[#5FA8FF] shrink-0 mt-0.5" />
                      <div className="flex-1 text-sm text-white/80 font-body">
                        <div className="font-display font-semibold text-white text-[13px] uppercase tracking-wider mb-0.5">Já tem conta?</div>
                        Inicie sessão para guardar este pedido na sua área e receber a resposta directamente no site — <a href="/login?next=/orcamento" className="text-[#FF5A00] hover:underline font-semibold">Iniciar sessão</a> ou <a href="/login?mode=register&next=/orcamento" className="text-[#FF5A00] hover:underline font-semibold">criar conta</a>. É opcional.
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Nome *
                      </label>
                      <input
                        data-testid="quote-input-nome"
                        type="text"
                        value={form.nome}
                        onChange={(e) => update("nome", e.target.value)}
                        placeholder="O seu nome"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Telefone *
                      </label>
                      {user && user.telefone ? (
                        <div
                          data-testid="quote-phone-account-pill"
                          className="flex items-center justify-between gap-3 bg-[#0E2452] border border-emerald-500/40 rounded-xl px-4 py-3"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-display">A associar à sua conta</div>
                              <div className="text-white font-body text-sm truncate">{user.telefone}</div>
                            </div>
                          </div>
                          <span className="hidden sm:inline text-[10px] text-white/40 font-body">automático</span>
                        </div>
                      ) : (
                        <input
                          data-testid="quote-input-telefone"
                          type="tel"
                          value={form.telefone}
                          onChange={(e) => update("telefone", e.target.value)}
                          placeholder="+351 9xx xxx xxx"
                          className={inputCls}
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                      Email *
                    </label>
                    {user ? (
                      <div
                        data-testid="quote-email-account-pill"
                        className="flex items-center justify-between gap-3 bg-[#0E2452] border border-emerald-500/40 rounded-xl px-4 py-3"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-display">A associar à sua conta</div>
                            <div className="text-white font-body text-sm truncate">{user.email}</div>
                          </div>
                        </div>
                        <span className="hidden sm:inline text-[10px] text-white/40 font-body">automático</span>
                      </div>
                    ) : (
                      <input
                        data-testid="quote-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="nome@exemplo.pt"
                        className={inputCls}
                        required
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Veículo (marca / modelo) *
                      </label>
                      <input
                        data-testid="quote-input-veiculo"
                        type="text"
                        value={form.veiculo}
                        onChange={(e) => update("veiculo", e.target.value)}
                        placeholder="Ex.: VW Golf 2019"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Matrícula <span className="text-white/40 normal-case tracking-normal text-[10px]">(opcional)</span>
                      </label>
                      <input
                        data-testid="quote-input-matricula"
                        type="text"
                        value={form.matricula}
                        onChange={(e) => update("matricula", e.target.value.toUpperCase())}
                        placeholder="Ex.: 12-AB-34"
                        maxLength={12}
                        className={`${inputCls} uppercase tracking-wider`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Serviço pretendido *
                      </label>
                      <select
                        data-testid="quote-input-servico"
                        value={form.servico}
                        onChange={(e) => update("servico", e.target.value)}
                        className={`${inputCls} appearance-none cursor-pointer`}
                        required
                      >
                        <option value="" className="bg-[#163A6D]">
                          Selecione um serviço
                        </option>
                        {SERVICE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt} className="bg-[#163A6D]">
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {form.servico === "Troca de Pneus" && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="md:col-span-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Medida do pneu *
                      </label>
                      <input
                        data-testid="quote-input-medida"
                        type="text"
                        value={form.medida_pneu}
                        onChange={(e) => update("medida_pneu", e.target.value)}
                        placeholder="Ex.: 205/55 R16"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                        Quantidade *
                      </label>
                      <input
                        data-testid="quote-input-quantidade"
                        type="number"
                        min={1}
                        max={20}
                        value={form.quantidade}
                        onChange={(e) => update("quantidade", e.target.value)}
                        className={inputCls}
                        required
                      />
                    </div>
                  </div>
                  )}

                  {form.servico === "Troca de Pneus" && (
                    <div
                      data-testid="quote-brand-preference"
                      className="border border-[#2F568F] bg-[#0E2452]/40 p-5"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display">
                          Tem preferência de marca?
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            data-testid="quote-brand-yes"
                            onClick={() => update("preferencia_marca", true)}
                            className={`px-6 py-2 font-display font-bold uppercase tracking-wider text-sm border transition-colors ${
                              form.preferencia_marca
                                ? "bg-[#FF5A00] border-[#FF5A00] text-black"
                                : "bg-transparent border-[#2F568F] text-white hover:border-[#FF5A00]"
                            }`}
                          >
                            Sim
                          </button>
                          <button
                            type="button"
                            data-testid="quote-brand-no"
                            onClick={() => {
                              update("preferencia_marca", false);
                              update("marca_preferida", "");
                            }}
                            className={`px-6 py-2 font-display font-bold uppercase tracking-wider text-sm border transition-colors ${
                              !form.preferencia_marca
                                ? "bg-[#FF5A00] border-[#FF5A00] text-black"
                                : "bg-transparent border-[#2F568F] text-white hover:border-[#FF5A00]"
                            }`}
                          >
                            Não
                          </button>
                        </div>
                      </div>

                      {form.preferencia_marca && (
                        <div className="mt-4">
                          <input
                            data-testid="quote-input-marca"
                            type="text"
                            value={form.marca_preferida}
                            onChange={(e) => update("marca_preferida", e.target.value)}
                            placeholder="Indique a marca preferida (ex.: Michelin, Continental, Pirelli...)"
                            className={inputCls}
                            autoFocus
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                      Anexar foto <span className="text-[#5A7AAE] normal-case tracking-normal">(opcional — livrete ou pneu atual)</span>
                    </label>

                    {form.anexo_base64 ? (
                      <div
                        data-testid="quote-attachment-preview"
                        className="flex items-center gap-3 border border-[#2F568F] bg-[#0E2452] p-3"
                      >
                        <img
                          src={form.anexo_base64}
                          alt="Pré-visualização do anexo"
                          className="w-16 h-16 object-cover border border-[#2F568F]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white font-body truncate">
                            {form.anexo_nome}
                          </div>
                          <div className="text-[11px] text-[#5A7AAE] font-body mt-0.5">
                            Pronto a enviar com o pedido
                          </div>
                        </div>
                        <button
                          data-testid="quote-attachment-remove"
                          type="button"
                          onClick={removeAttachment}
                          className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-transparent hover:bg-[#FF5A00] border border-[#2F568F] hover:border-[#FF5A00] text-white transition-colors"
                          aria-label="Remover anexo"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="quote-attachment-input"
                        data-testid="quote-attachment-trigger"
                        className="flex items-center justify-center gap-2 w-full border border-dashed border-[#2F568F] bg-[#0E2452]/40 hover:bg-[#0E2452] hover:border-[#FF5A00] text-[#A1A1AA] hover:text-white cursor-pointer py-4 px-4 transition-colors font-body text-sm"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            A processar...
                          </>
                        ) : (
                          <>
                            <Paperclip className="w-4 h-4" />
                            Carregar imagem (JPG, PNG · máx. 5MB)
                          </>
                        )}
                      </label>
                    )}
                    <input
                      ref={fileInputRef}
                      id="quote-attachment-input"
                      data-testid="quote-attachment-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFilePick}
                      className="hidden"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-display block mb-2">
                      Mensagem
                    </label>
                    <textarea
                      data-testid="quote-input-mensagem"
                      value={form.mensagem}
                      onChange={(e) => update("mensagem", e.target.value)}
                      placeholder="Detalhes adicionais: marca de pneu preferida, urgência, outros serviços..."
                      rows={4}
                      className={`${inputCls} h-auto py-3 resize-none`}
                    />
                  </div>

                  <label className="flex items-start gap-3 bg-[#0E2452] border border-[#2F568F]/60 rounded-xl px-4 py-3 cursor-pointer hover:border-[#FF5A00]/60 transition-colors">
                    <input
                      data-testid="quote-notify-toggle"
                      type="checkbox"
                      checked={form.notify_pref === "email"}
                      onChange={(e) => update("notify_pref", e.target.checked ? "email" : "none")}
                      className="mt-1 w-4 h-4 accent-[#FF5A00] cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="text-white font-body text-sm">Notificar-me por email quando a oficina responder</div>
                      <div className="text-white/50 font-body text-xs mt-0.5">Receberá um email com um botão "Ver resposta" que abre diretamente o orçamento na sua conta.</div>
                    </div>
                  </label>

                  <button
                    data-testid="quote-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full text-base py-5 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        A enviar...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Enviar Pedido
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#5A7AAE] font-body text-center">
                    * Campos obrigatórios. Os seus dados só serão utilizados para resposta
                    a este pedido.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
